import React, { useRef, useState } from 'react';
import { ShoppingBag } from 'lucide-react';
import { type CodCheckoutInputDto, type CodFieldValue, type CodFormConfig, type StorefrontUpsell } from '@xshop/shared';
import { cartMoney } from '../commerce/cartMoney';
import { trackEvent } from '../tracking/tracker';
import { useTakesCards } from '../commerce/useTakesCards';
import { PaymentChoice, type CheckoutPayment } from './PaymentChoice';
import { CodFormFields } from './CodFormFields';
import { CheckoutTotals } from './CheckoutTotals';
import { OrderSubmit } from './OrderSubmit';
import { codFormLook, orderButtonText, secureNoteText } from './codFormLook';
import type { OfferSaving } from '../commerce/offerSavings';
import { checkoutLanguage, languageSubmitText, resolveCheckoutLabels, type CheckoutDirection, type CheckoutLabels, type CheckoutLanguage } from './checkoutLabels';
import { UpsellBump } from './UpsellBump';
import { useProductUpsell } from './useProductUpsell';
import { DiscountCodeField } from './DiscountCodeField';
import { CheckoutPolicyLinks } from './CheckoutPolicyLinks';
import { useCheckoutValues } from './useCheckoutValues';
import { useCheckoutMoney } from './useCheckoutMoney';
import { useCheckoutSubmit } from './useCheckoutSubmit';

/**
 * What the page's own checkout elements collected, when the merchant built the form out of them. Given
 * one, this form stops holding a second copy of the shopper's answers and reads and writes that one:
 * the address block, the variant picker and the order bump on the page are then answering the order
 * being placed here rather than collecting into nothing.
 */
export type CheckoutAnswers = {
  values: Readonly<Record<string, CodFieldValue>>;
  setValue: (key: string, value: CodFieldValue) => void;
  acceptedOfferId: string | null;
  acceptOffer: (id: string | null) => void;
};

interface OnePageCheckoutProps {
  config: CodFormConfig | null;
  /** A note the shopper wrote in the cart, sent with the order beside the form's own answers. */
  orderNote?: string;
  offerOverride?: StorefrontUpsell | null;
  initialAcceptedOfferId?: string | null;
  lineItems?: CodCheckoutInputDto['lineItems'];
  /** The cart's lines as the server priced them, which a discount code is checked against; one line from the props otherwise. */
  pricedLines?: Array<{ productId: string; quantity: number; lineTotal: number }>;
  /** What the store's quantity offers took off ("Pack of 3"), listed under a subtotal at full price. */
  offerSavings?: OfferSaving[];
  productId: string;
  variantId: string;
  lineTotal: number;
  quantity: number;
  currency?: string;
  // A theme in another language overrides these; the defaults follow the theme's declared direction.
  submitLabel?: string;
  secureNote?: string;
  labels?: Partial<CheckoutLabels>;
  /** The theme's declared direction, which lays the card out and picks its language when the store chose none. */
  direction?: CheckoutDirection;
  /** The language the store chose to be served in; every string a theme left unset is printed in it. */
  language?: CheckoutLanguage | null;
  /** The page's shared answers, when its own elements are collecting them; this form owns them otherwise. */
  answers?: CheckoutAnswers | null;
  /** True in the builder and a theme preview: the form can be filled in to see it work, but never sends. */
  preview?: boolean;
  /** True while the order is being priced again: the form stays, but cannot be sent on the old price. */
  busy?: boolean;
  onSuccess: (orderData: any) => void;
}

/** The products the form orders, as the pixels list them. */
type PixelContents = Array<{ id: string; quantity: number }>;

// Marks the checkout funnel step the first time the visitor puts focus in the form, never in a preview.
function useCheckoutStart(preview: boolean, event: { productId: string; value: number; currency: string; numItems: number; contents: PixelContents }) {
  const started = useRef(false);
  return () => {
    if (preview || started.current) return;
    started.current = true;
    trackEvent('begin_checkout', event);
  };
}

// The card's own heading, unless a theme band prints its own.
const CheckoutHeading: React.FC<{ heading: string }> = ({ heading }) => (
  <div className="checkout-header">
    <ShoppingBag size={20} color="#059669" />
    <h2>{heading}</h2>
  </div>
);

// Says above the form why it was not sent.
const FormError: React.FC<{ message: string }> = ({ message }) => (
  <div role="alert" dir="auto" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: 12, borderRadius: 8, fontSize: 14 }}>
    {message}
  </div>
);

// Reads what the form orders: its lines (the cart's, or the one product), how many units, and the pixels' list.
function orderedOf(props: Pick<OnePageCheckoutProps, 'lineItems' | 'variantId' | 'productId' | 'quantity'>) {
  const lineItems = props.lineItems ?? [{ variantId: props.variantId, productId: props.productId, quantity: props.quantity }];
  const contents: PixelContents = lineItems.filter((row) => row.productId).map((row) => ({ id: row.productId, quantity: row.quantity }));
  return { lineItems, contents, units: lineItems.reduce((sum, row) => sum + row.quantity, 0) };
}

// The one cash-on-delivery form, rendering exactly the fields the merchant configured, in whichever placement holds it.
export const OnePageCheckout: React.FC<OnePageCheckoutProps> = (props) => {
  const { config, lineTotal, quantity, currency = 'MAD', labels, direction = 'ltr', preview = false, busy = false } = props;
  // Every remaining string the card prints: the theme's own wording first, the store's language otherwise.
  const lang = checkoutLanguage(props.language ?? null, direction);
  const text = resolveCheckoutLabels(labels, lang);
  const fallback = languageSubmitText(lang);
  const look = codFormLook(config?.style);
  const takesCards = useTakesCards();
  // Cash on delivery until the shopper picks card, which only a store that takes cards offers.
  const [payment, setPayment] = useState<CheckoutPayment>('COD');
  // Read whether or not an override arrives, so the hooks run in the same order every time.
  const productOffer = useProductUpsell(props.productId);
  const offer = props.offerOverride ?? productOffer;
  const answers = useCheckoutValues({ answers: props.answers ?? null, initialAcceptedOfferId: props.initialAcceptedOfferId, offer, productId: props.productId, currency });
  const ordered = orderedOf(props);
  const money = useCheckoutMoney({ lineTotal, acceptedOffer: answers.acceptedOffer, preview, codeLines: props.pricedLines ?? [{ productId: props.productId, quantity, lineTotal }], language: lang });
  const send = useCheckoutSubmit({
    config, preview, text, values: answers.values, setErrors: answers.setErrors, onSuccess: props.onSuccess,
    order: { lineItems: ordered.lineItems, acceptedOfferId: answers.acceptedOffer?.id, discountCode: money.discount.applied?.code, orderNote: props.orderNote, payment },
    discount: { refuse: money.discount.refuse, label: money.discountRow?.label ?? null },
    pixels: { contents: ordered.contents, currency }
  });
  const start = useCheckoutStart(preview, { productId: props.productId, value: lineTotal, currency, numItems: ordered.units, contents: ordered.contents });
  // The card's numbers follow the theme's declared direction, so an Arabic page never mixes "DH" into "د.م.".
  const displayPrice = (val: number) => cartMoney(val, currency, direction);
  const total = money.totalPrice === null ? null : displayPrice(money.totalPrice);
  // A theme band that prints its own heading or reassurance line passes them as '' to silence the card's.
  const note = props.secureNote === '' ? null : secureNoteText(config?.style, props.secureNote ?? fallback.secureNote);

  return (
    <div className={['checkout-card', look.className].filter(Boolean).join(' ')} id="checkout-section" style={look.vars}>
      {labels?.heading !== '' && <CheckoutHeading heading={text.heading} />}
      {send.formError && <FormError message={send.formError} />}
      <form id="xshop-cod-form" onSubmit={send.submit} onFocusCapture={start} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {config ? <CodFormFields config={config} values={answers.values} errors={answers.errors} onChange={answers.change} /> : <div style={{ color: '#64748b', fontSize: 14 }}>{text.loading}</div>}
        <PaymentChoice takesCards={takesCards} value={payment} onChange={setPayment} language={lang} cashTitle={text.codNoticeTitle} cashText={text.codNoticeText} />
        {offer && <UpsellBump offer={offer} accepted={answers.acceptedOffer !== null} format={displayPrice} onToggle={answers.toggleBump} />}
        {money.discount.available && <DiscountCodeField discount={money.discount} words={money.codeWords} />}
        <CheckoutTotals quantity={quantity} merchandiseTotal={lineTotal} offers={props.offerSavings} format={displayPrice} labels={text}
          bump={answers.acceptedOffer ? { label: answers.acceptedOffer.productTitle, amount: answers.acceptedOffer.unitPrice } : null}
          discount={money.discountRow} shippingFee={money.shippingFee} total={money.totalPrice} />
        <OrderSubmit label={orderButtonText(config?.style, props.submitLabel || fallback.submitLabel, total)} submittingLabel={text.submitting}
          submitting={send.submitting} disabled={send.submitting || !config || busy} note={note} />
        <CheckoutPolicyLinks language={lang} />
      </form>
    </div>
  );
};
